/**
 * 接口请求封装
 * 支持 GET、POST，请求头 Header 中使用 token 参数携带令牌
 */

import { getDeApiErrorMessage } from './digitalEmployeeProject'

const TOKEN_KEY = 'ds-token'
const TOKEN_EXPIRES_KEY = 'ds-token-expires'

const DEFAULT_LEGACY_API_BASE = 'https://www.dsagent.cn/agent-dev/'
const DEFAULT_SITE_ROOT_API_BASE = 'https://www.dsagent.cn/'

function getConfiguredBaseUrl() {
  return (
    (typeof window !== 'undefined' && window.__API_BASE_URL__) ||
    process.env.VUE_APP_API_BASE_URL ||
    DEFAULT_LEGACY_API_BASE
  )
}

/** 原有业务接口基址（登录、agentHub、workflow 等） */
export function getLegacyApiBaseUrl() {
  return getConfiguredBaseUrl()
}

/** 站点根路径 API 基址（数字员工 /ai_employee/*） */
export function getSiteRootApiBaseUrl() {
  if (typeof window !== 'undefined' && window.__SITE_ROOT_API_BASE_URL__) {
    return window.__SITE_ROOT_API_BASE_URL__
  }
  if (process.env.VUE_APP_SITE_ROOT_API_BASE_URL) {
    return process.env.VUE_APP_SITE_ROOT_API_BASE_URL
  }
  const base = getConfiguredBaseUrl()
  if (typeof base === 'string' && /^https?:\/\//i.test(base)) {
    try {
      const u = new URL(base)
      return `${u.origin}/`
    } catch {
      return DEFAULT_SITE_ROOT_API_BASE
    }
  }
  return DEFAULT_SITE_ROOT_API_BASE
}

function resolveRequestBaseUrl(options = {}) {
  return options.apiScope === 'siteRoot' ? getSiteRootApiBaseUrl() : getLegacyApiBaseUrl()
}

/**
 * 获取 token（未登录时为空字符串，勿使用占位默认值以免干扰鉴权与路由守卫）
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || ''
}

/**
 * 设置 token（登录成功后调用）
 * @param {string} token - 令牌
 * @param {number} [expiresIn] - 有效期（秒），不传则不记录过期时间
 */
export function setToken(token, expiresIn) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
    if (typeof expiresIn === 'number' && expiresIn > 0) {
      const expiresAt = Date.now() + expiresIn * 1000
      localStorage.setItem(TOKEN_EXPIRES_KEY, String(expiresAt))
    } else {
      localStorage.removeItem(TOKEN_EXPIRES_KEY)
    }
  } else {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(TOKEN_EXPIRES_KEY)
  }
}

/**
 * 判断 token 是否过期
 * 若未存储过期时间，则视为未过期
 */
export function isTokenExpired() {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_KEY)
  if (!expiresAt) return false
  return Date.now() >= Number(expiresAt)
}

/** 跳转登录页时展示的统一提示（与 LoginPage 文案呼应） */
export const AUTH_RELOGIN_MESSAGE = '登录状态已失效，请重新登录'

/**
 * 清除 token 并跳转登录页（供 401 / 业务层鉴权失败时调用）
 */
export function clearTokenAndRedirect() {
  setToken('')
  if (typeof window !== 'undefined') {
    const base = (process.env.BASE_URL || '/').replace(/\/$/, '')
    const redirect = encodeURIComponent(location.pathname + location.search)
    window.location.href = `${location.origin}${base}/login?redirect=${redirect}&expired=1`
  }
}

/** 业务码：未登录 / token 无效（可按后端约定追加；勿滥用 403 以免与普通权限不足混淆） */
const UNAUTHORIZED_BIZ_CODES = new Set([401, '401', 10001, '10001', 10002, '10002'])

/**
 * 判断接口 JSON 是否表示「无 token / token 过期 / 需重新登录」
 * 覆盖 HTTP 200 但 body.code 非成功、或错误信息含鉴权关键词等情况
 */
export function isUnauthorizedBizResponse(data) {
  if (data == null || typeof data !== 'object') return false
  const msgHead = `${data.msg ?? ''} ${data.message ?? ''}`
  /** 业务 401「未绑定手机号」：登录态有效但需补绑，不应触发整站重新登录跳转 */
  if (UNAUTHORIZED_BIZ_CODES.has(data.code)) {
    if ((data.code === 401 || data.code === '401') && /未绑定手机号/.test(msgHead)) {
      return false
    }
    return true
  }
  const text = `${msgHead} ${data.error ?? ''} ${data.errorMessage ?? ''}`
  if (!text.trim()) return false
  return /未登录|请先登录|需要登录|登录已过期|登录状态已失效|token\s*已过期|token过期|无\s*token|缺少\s*token|未携带\s*token|未传\s*token|token\s*无效|无效\s*token|令牌已过期|令牌失效|鉴权失败|身份验证失败|未授权访问|认证失败|凭证无效|凭证已失效/i.test(
    text
  )
}

function rejectAuthAndRedirect() {
  clearTokenAndRedirect()
  return Promise.reject(new Error(AUTH_RELOGIN_MESSAGE))
}

/**
 * 仅携带 token（用于 GET 下载等无 body 的请求，避免 Content-Type 导致网关/鉴权异常）
 */
function buildTokenHeaders() {
  const token = getToken()
  const headers = { Accept: '*/*' }
  if (token) headers.token = token
  return headers
}

/**
 * 构建请求头，包含 token
 */
function buildHeaders(customHeaders = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...customHeaders
  }
  const token = getToken()
  if (token) {
    headers.token = token
  }
  return headers
}

/**
 * 发起请求（body 为 FormData 时不设置 Content-Type，由浏览器自动带 multipart 边界；Header 仍携带 token）
 */
function request(url, options = {}) {
  const { apiScope, ...fetchOptions } = options
  const base = resolveRequestBaseUrl({ apiScope })
  const fullUrl = url.startsWith('http')
    ? url
    : `${base.replace(/\/$/, '')}/${url.replace(/^\//, '')}`
  const body = fetchOptions.body
  const isFormData = body instanceof FormData
  const headers = isFormData
    ? (() => {
        const h = { Accept: 'application/json', ...(fetchOptions.headers || {}) }
        const token = getToken()
        if (token) h.token = token
        delete h['Content-Type']
        return h
      })()
    : buildHeaders(fetchOptions.headers || {})

  const config = {
    ...fetchOptions,
    headers,
  }
  if (!isFormData && config.headers['Content-Type'] === 'application/json' && config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body)
  }
  return fetch(fullUrl, config).then(async (res) => {
    const contentType = res.headers.get('content-type')
    const isJson = contentType && contentType.includes('application/json')
    const data = isJson ? await res.json() : await res.text()
    if (!res.ok) {
      if (res.status === 401) {
        return rejectAuthAndRedirect()
      }
      if (typeof data === 'object' && data !== null && isUnauthorizedBizResponse(data)) {
        return rejectAuthAndRedirect()
      }
      const err = new Error(getDeApiErrorMessage(data, `请求失败 ${res.status}`))
      err.status = res.status
      err.data = data
      throw err
    }
    if (isJson && typeof data === 'object' && data !== null && isUnauthorizedBizResponse(data)) {
      return rejectAuthAndRedirect()
    }
    // 数字员工等接口：HTTP 200 但 success:false（如多模态 error.message）
    if (isJson && typeof data === 'object' && data !== null && data.success === false) {
      const err = new Error(getDeApiErrorMessage(data, '请求失败'))
      err.data = data
      throw err
    }
    return data
  })
}

/** 查询参数值中无需编码的字符（含空格、冒号，用于 yyyy-MM-dd HH:mm:ss） */
const GET_QUERY_VALUE_SAFE_RE = /[A-Za-z0-9\-:._ ]/

/**
 * 编码 GET 查询参数值：仅编码会破坏查询串的字符及中文等，空格与冒号保持原文，不使用 + 号。
 */
function encodeGetQueryValue(value) {
  const str = String(value).trim().replace(/\+/g, ' ').replace(/%20/g, ' ')
  let out = ''
  for (const ch of str) {
    out += GET_QUERY_VALUE_SAFE_RE.test(ch) ? ch : encodeURIComponent(ch)
  }
  return out
}

/**
 * 序列化 GET 查询参数对象为查询字符串
 * @param {Object} params
 * @returns {string}
 */
export function serializeGetParams(params = {}) {
  const parts = []
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return
    parts.push(`${encodeURIComponent(key)}=${encodeGetQueryValue(value)}`)
  })
  return parts.join('&')
}

/**
 * GET 请求
 * @param {string} url - 请求地址（相对路径会拼接 BASE_URL）
 * @param {Object} [params] - URL 查询参数
 * @param {Object} [options] - 其他 fetch 选项（如 headers）
 */
export function get(url, params = {}, options = {}) {
  const query = serializeGetParams(params)
  const fullUrl = query ? `${url}?${query}` : url
  return request(fullUrl, { method: 'GET', ...options })
}

/**
 * POST 请求
 * @param {string} url - 请求地址
 * @param {Object|FormData} [data] - 请求体（对象会序列化为 JSON；FormData 用于文件上传）
 * @param {Object} [options] - 其他 fetch 选项
 */
export function post(url, data = {}, options = {}) {
  return request(url, {
    method: 'POST',
    body: data,
    ...options
  })
}

/**
 * PUT 请求
 */
export function put(url, data = {}, options = {}) {
  return request(url, {
    method: 'PUT',
    body: data,
    ...options
  })
}

/**
 * DELETE 请求
 */
export function del(url, options = {}) {
  return request(url, { method: 'DELETE', ...options })
}

/**
 * 从响应头 Content-Disposition 解析文件名（支持 filename* UTF-8 编码）
 */
export function parseContentDispositionFilename(cd, fallback = 'download') {
  if (!cd || typeof cd !== 'string') return fallback
  const star = /filename\*=(?:UTF-8''|utf-8'')([^;\n\r]+)/i.exec(cd)
  if (star) {
    try { return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, '')) } catch (_) { /* ignore */ }
  }
  const plain = /filename\s*=\s*("?)([^";\n\r]+)\1/i.exec(cd)
  if (plain && plain[2]) return plain[2].trim()
  return fallback
}

/**
 * 触发浏览器将 Blob 另存为本地文件
 */
export function saveBlobAsFile(blob, filename) {
  if (typeof window === 'undefined' || !blob) return
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'download'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function resolveDownloadFilename(name, url) {
  const base = String(name || 'download').trim() || 'download'
  if (/\.(jpe?g|png|gif|webp|bmp)$/i.test(base)) return base
  const m = String(url || '').match(/\.(jpe?g|png|gif|webp|bmp)(\?|#|$)/i)
  if (m) {
    const ext = m[1].toLowerCase() === 'jpeg' ? 'jpg' : m[1].toLowerCase()
    return `${base}.${ext}`
  }
  return `${base}.jpg`
}

function downloadImageViaCanvas(url, filename) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('无法创建画布'))
          return
        }
        ctx.drawImage(img, 0, 0)
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('无法导出图片'))
            return
          }
          saveBlobAsFile(blob, filename)
          resolve()
        }, 'image/png')
      } catch (err) {
        reject(err)
      }
    }
    img.onerror = () => reject(new Error('图片加载失败'))
    img.src = url
  })
}

/**
 * 将远程/本地图片 URL 下载到本地（跨域时先 fetch/canvas 转 Blob，避免页面跳转）
 * @param {string} url
 * @param {string} [filename]
 */
export async function downloadUrlAsFile(url, filename = 'download') {
  const src = String(url || '').trim()
  if (!src) throw new Error('下载地址为空')
  const name = resolveDownloadFilename(filename, src)

  if (src.startsWith('blob:') || src.startsWith('data:')) {
    saveBlobAsFile(await (await fetch(src)).blob(), name)
    return
  }

  try {
    const res = await fetch(src, { mode: 'cors', credentials: 'omit', cache: 'no-store' })
    if (!res.ok) throw new Error(`下载失败 (${res.status})`)
    const blob = await res.blob()
    if (!blob || !blob.size) throw new Error('图片内容为空')
    saveBlobAsFile(blob, name)
    return
  } catch (_) {
    await downloadImageViaCanvas(src, name)
  }
}

/**
 * GET 下载二进制文件（带 token Header，不将响应当 JSON 解析）
 * 文件名优先从响应头 Content-Disposition 解析（与后端一致）。
 * @param {string} url - 相对路径或绝对 URL
 * @param {Record<string, string>} [params] - 查询参数
 * @param {string} [fallbackFilename='download'] - 无 Content-Disposition 时的回退文件名
 * @param {string} [forcedFilename] - 若为非空字符串，则始终使用该文件名（忽略 Content-Disposition）
 * @returns {Promise<void>}
 */
export async function getFileDownload(url, params = {}, fallbackFilename = 'download', forcedFilename = '') {
  const query = serializeGetParams(params)
  const pathWithQuery = query ? `${url}?${query}` : url
  const fullUrl = pathWithQuery.startsWith('http')
    ? pathWithQuery
    : `${getLegacyApiBaseUrl().replace(/\/$/, '')}/${pathWithQuery.replace(/^\//, '')}`

  const res = await fetch(fullUrl, {
    method: 'GET',
    headers: buildTokenHeaders(),
    mode: 'cors',
    cache: 'no-store',
  })

  if (!res.ok) {
    if (res.status === 401) {
      clearTokenAndRedirect()
      throw new Error(AUTH_RELOGIN_MESSAGE)
    }
    const ct = res.headers.get('content-type') || ''
    const body = await res.text()
    let msg = `下载失败 ${res.status}`
    if (ct.includes('application/json')) {
      try {
        const j = JSON.parse(body)
        if (isUnauthorizedBizResponse(j)) {
          clearTokenAndRedirect()
          throw new Error(AUTH_RELOGIN_MESSAGE)
        }
        msg = j?.msg || j?.message || msg
      } catch (e) {
        if (e.message === AUTH_RELOGIN_MESSAGE) throw e
      }
    } else if (body) {
      msg = body.slice(0, 200)
    }
    throw new Error(msg)
  }

  const ctOk = res.headers.get('content-type') || ''
  if (ctOk.includes('application/json')) {
    const text = await res.text()
    let j
    try {
      j = JSON.parse(text)
    } catch {
      throw new Error('下载响应异常')
    }
    if (isUnauthorizedBizResponse(j)) {
      clearTokenAndRedirect()
      throw new Error(AUTH_RELOGIN_MESSAGE)
    }
    throw new Error(j?.msg || j?.message || '下载失败')
  }

  const blob = await res.blob()
  const cd = res.headers.get('content-disposition') || ''
  const forced = typeof forcedFilename === 'string' && forcedFilename.trim()
  const filename = forced
    ? forcedFilename.trim()
    : parseContentDispositionFilename(cd, fallbackFilename)
  saveBlobAsFile(blob, filename)
}

/**
 * @deprecated 请使用 {@link getFileDownload}
 */
export async function downloadBlob(url, params = {}, fallbackFilename = 'download', forcedFilename = '') {
  return getFileDownload(url, params, fallbackFilename, forcedFilename)
}
